import { notification } from "antd";
import { get, isNil, noop } from "lodash";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { useMutation, useQuery } from "shared/hooks/useApi";
import { NAME_SPACES } from "shared/locales/constants";
import graphql from "utils/api/graphql";
import { DOCUMENT_KINDS, SCREENS } from "utils/constants";
import { searchable } from "utils/helpers/text";
import useStaticDocumentDownload from "../../shared/useStaticDocumentDownload";
import View, { usageNames, ROW_ACTIONS } from "./View";

const DOCUMENTS_LIMIT = 500;

const matchesSearch = (document, search) =>
  searchable(`${document.name} ${usageNames(document)}`).includes(
    searchable(search),
  );

const StaticDocuments = () => {
  const { t } = useTranslation(NAME_SPACES.PRIVATE.ADMIN.DYNAMIC_DOCUMENTS);
  const navigate = useNavigate();
  const download = useStaticDocumentDownload();
  const [search, setSearch] = useState("");
  const [deleting, setDeleting] = useState(null);

  const { data, loading, refetch } = useQuery(
    graphql.queries.STATIC_DOCUMENTS,
    {
      variables: { skip: 0, take: DOCUMENTS_LIMIT },
      fetchPolicy: "network-only",
    },
  );
  const documents = useMemo(
    () => get(data, "staticDocuments.data") || [],
    [data],
  );
  const visible = useMemo(
    () => documents.filter((document) => matchesSearch(document, search)),
    [documents, search],
  );

  const [deleteDocument, { loading: removing }] = useMutation(
    graphql.mutations.DELETE_STATIC_DOCUMENT,
    { onError: noop },
  );

  const open = ({ id }) =>
    navigate(`${SCREENS.PRIVATE.ADMIN.DYNAMIC_DOCUMENTS.EDIT.path}/${id}`, {
      state: { kind: DOCUMENT_KINDS.STATIC },
    });

  const confirmDelete = async () => {
    if (isNil(deleting) || removing) return;
    const { data: deleted } = await deleteDocument({
      variables: { where: { id: deleting.id } },
    });
    setDeleting(null);
    if (isNil(get(deleted, "deleteStaticDocument.id"))) {
      notification.error({ message: t("STATIC.DELETE.FAILED") });
      return;
    }
    notification.success({ message: t("STATIC.DELETE.DONE") });
    refetch();
  };

  const actions = {
    [ROW_ACTIONS.DOWNLOAD]: (document) => download(document),
    [ROW_ACTIONS.DELETE]: setDeleting,
  };

  return (
    <View
      loaded={!isNil(data)}
      loading={loading}
      documents={documents}
      visible={visible}
      search={search}
      onSearch={setSearch}
      onOpen={open}
      onAction={(key, document) => actions[key]?.(document)}
      deleting={deleting}
      onConfirmDelete={confirmDelete}
      onCancelDelete={() => setDeleting(null)}
    />
  );
};

export default StaticDocuments;
